import React, { useEffect, useMemo, useRef, useState } from 'react'
import { EditorContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TextAlign } from '@tiptap/extension-text-align'
import './AdminHtmlEditor.css'

function ToolbarButton({ active = false, disabled = false, title, onClick, children }) {
  return <button
    type="button"
    className={`admin-html-editor__tool${active ? ' is-active' : ''}`}
    disabled={disabled}
    title={title}
    aria-pressed={active ? 'true' : 'false'}
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  >{children}</button>
}

function normalizeLink(value) {
  const next = String(value || '').trim()
  if (!next) return ''
  if (/^(https?:|mailto:|tel:|\/|#)/i.test(next)) return next
  return `https://${next}`
}

const RAW_ONLY_TAGS = /<(?:div|section|article|main|header|footer|nav|aside|figure|figcaption|img|picture|source|video|audio|iframe|table|thead|tbody|tfoot|tr|th|td|colgroup|col|style|details|summary|dl|dt|dd|h1|h5|h6)\b/i
const RAW_ONLY_ATTRS = /\s(?:class|id|data-[\w:-]+)\s*=/i
const ESCAPED_HTML_TAGS = /&lt;\/?(?:div|section|article|main|header|footer|nav|aside|figure|figcaption|img|picture|video|audio|iframe|table|thead|tbody|tfoot|tr|th|td|style|details|summary|h[1-6]|p|ul|ol|li)\b/gi

function hasNonEditorStyle(value) {
  const html = String(value || '')
  const pattern = /\sstyle\s*=\s*(["'])(.*?)\1/gi
  let match
  while ((match = pattern.exec(html))) {
    const declarations = String(match[2] || '').split(';').map((item) => item.trim()).filter(Boolean)
    if (declarations.some((declaration) => !/^text-align\s*:\s*(?:left|center|right|justify)$/i.test(declaration))) return true
  }
  return false
}

function looksLikeEscapedHtml(value) {
  const matches = String(value || '').match(ESCAPED_HTML_TAGS)
  return Boolean(matches && matches.length >= 2)
}

function requiresRawHtml(value) {
  const html = String(value || '')
  return RAW_ONLY_TAGS.test(html) || RAW_ONLY_ATTRS.test(html) || hasNonEditorStyle(html) || looksLikeEscapedHtml(html)
}

function recoverEscapedHtml(value) {
  const html = String(value || '').trim()
  if (!looksLikeEscapedHtml(html)) return ''
  const wrapped = html.match(/^<p>([\s\S]*)<\/p>$/i)
  if (!wrapped) return ''
  const decoded = wrapped[1]
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(?:39|x27);/gi, "'")
    .replace(/&amp;/gi, '&')
    .trim()
  return /<(?:div|section|article|h[1-6]|p|ul|ol|table)\b/i.test(decoded) ? decoded : ''
}

function rawPreviewDocument(bodyHtml) {
  return `<!doctype html><html lang="ko"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><base target="_blank"><style>html,body{margin:0;background:#fff;color:#25211d;font-family:Pretendard,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif}body{padding:24px;overflow-wrap:anywhere}*{box-sizing:border-box}img,video{max-width:100%;height:auto}iframe,object,embed,form{display:none!important}</style></head><body>${bodyHtml || '<p>등록된 본문이 없습니다.</p>'}</body></html>`
}

export default function AdminHtmlEditor({
  value = '',
  onChange,
  required = false,
  minHeight = 300,
  ariaLabel = 'HTML 본문',
}) {
  const [sourceMode, setSourceMode] = useState(() => requiresRawHtml(value))
  const sourceModeRef = useRef(sourceMode)
  const onChangeRef = useRef(onChange)
  const rawHtml = useMemo(() => requiresRawHtml(value), [value])
  const recoveredHtml = useMemo(() => recoverEscapedHtml(value), [value])

  useEffect(() => { sourceModeRef.current = sourceMode }, [sourceMode])
  useEffect(() => { onChangeRef.current = onChange }, [onChange])

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: { levels: [2, 3, 4] },
      link: {
        openOnClick: false,
        autolink: true,
        linkOnPaste: true,
        defaultProtocol: 'https',
        HTMLAttributes: { rel: 'noopener noreferrer' },
      },
    }),
    TextAlign.configure({ types: ['heading', 'paragraph'] }),
  ], [])

  const editor = useEditor({
    extensions,
    content: requiresRawHtml(value) ? '' : (value || ''),
    editorProps: {
      attributes: {
        class: 'admin-html-editor__content',
        'aria-label': ariaLabel,
      },
    },
    onUpdate: ({ editor: currentEditor }) => {
      if (sourceModeRef.current) return
      onChangeRef.current?.(currentEditor.getHTML())
    },
  })

  useEffect(() => {
    if (!editor || sourceMode || rawHtml) return
    const next = value || ''
    if (next !== editor.getHTML()) editor.commands.setContent(next, { emitUpdate: false })
  }, [editor, value, sourceMode, rawHtml])

  const visualEditorDisabled = !editor || rawHtml

  const setLink = () => {
    if (!editor || rawHtml) return
    const previous = editor.getAttributes('link')?.href || ''
    const entered = window.prompt('링크 URL을 입력해주세요.', previous)
    if (entered === null) return
    const href = normalizeLink(entered)
    if (!href) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run()
      return
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href }).run()
  }

  const toggleSource = () => {
    if (sourceMode && !rawHtml && editor) editor.commands.setContent(value || '', { emitUpdate: false })
    setSourceMode((current) => !current)
  }

  const recoverSource = () => {
    if (!recoveredHtml) return
    if (!window.confirm('현재 본문은 HTML 코드가 일반 텍스트로 변환된 형태입니다. 태그를 실제 HTML로 복구할까요?')) return
    onChangeRef.current?.(recoveredHtml)
    setSourceMode(true)
  }

  const empty = !String(value || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/gi, ' ').trim()
  const footerMessage = sourceMode
    ? rawHtml
      ? '원본 HTML 보존 모드입니다. 입력한 태그·속성·인라인 스타일을 시각 편집기로 재변환하지 않습니다.'
      : 'HTML 소스를 직접 수정하는 모드입니다.'
    : rawHtml
      ? '원본 HTML을 변환하지 않고 렌더링만 확인하는 미리보기입니다.'
      : '화면에서 편집하면 HTML로 저장됩니다.'

  return <div className={`admin-html-editor${sourceMode ? ' is-source' : ''}`} data-admin-html-editor="true" data-required={required ? 'true' : 'false'}>
    <div className="admin-html-editor__toolbar" role="toolbar" aria-label="본문 서식 도구">
      <div className="admin-html-editor__tool-group">
        <ToolbarButton title="일반 문단" active={Boolean(editor?.isActive('paragraph'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().setParagraph().run()}>본문</ToolbarButton>
        <ToolbarButton title="제목 2" active={Boolean(editor?.isActive('heading', { level: 2 }))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}>H2</ToolbarButton>
        <ToolbarButton title="제목 3" active={Boolean(editor?.isActive('heading', { level: 3 }))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}>H3</ToolbarButton>
      </div>

      <div className="admin-html-editor__tool-group">
        <ToolbarButton title="굵게" active={Boolean(editor?.isActive('bold'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleBold().run()}><strong>B</strong></ToolbarButton>
        <ToolbarButton title="기울임" active={Boolean(editor?.isActive('italic'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleItalic().run()}><em>I</em></ToolbarButton>
        <ToolbarButton title="밑줄" active={Boolean(editor?.isActive('underline'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleUnderline().run()}><u>U</u></ToolbarButton>
        <ToolbarButton title="취소선" active={Boolean(editor?.isActive('strike'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleStrike().run()}><s>S</s></ToolbarButton>
      </div>

      <div className="admin-html-editor__tool-group">
        <ToolbarButton title="글머리 목록" active={Boolean(editor?.isActive('bulletList'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleBulletList().run()}>• 목록</ToolbarButton>
        <ToolbarButton title="번호 목록" active={Boolean(editor?.isActive('orderedList'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleOrderedList().run()}>1. 목록</ToolbarButton>
        <ToolbarButton title="인용문" active={Boolean(editor?.isActive('blockquote'))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().toggleBlockquote().run()}>“ 인용</ToolbarButton>
      </div>

      <div className="admin-html-editor__tool-group">
        <ToolbarButton title="왼쪽 정렬" active={Boolean(editor?.isActive({ textAlign: 'left' }))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().setTextAlign('left').run()}>왼쪽</ToolbarButton>
        <ToolbarButton title="가운데 정렬" active={Boolean(editor?.isActive({ textAlign: 'center' }))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().setTextAlign('center').run()}>가운데</ToolbarButton>
        <ToolbarButton title="오른쪽 정렬" active={Boolean(editor?.isActive({ textAlign: 'right' }))} disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().setTextAlign('right').run()}>오른쪽</ToolbarButton>
      </div>

      <div className="admin-html-editor__tool-group">
        <ToolbarButton title="링크 설정" active={Boolean(editor?.isActive('link'))} disabled={visualEditorDisabled} onClick={setLink}>링크</ToolbarButton>
        <ToolbarButton title="구분선" disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().setHorizontalRule().run()}>구분선</ToolbarButton>
        <ToolbarButton title="서식 지우기" disabled={visualEditorDisabled} onClick={() => editor?.chain().focus().unsetAllMarks().clearNodes().run()}>서식 지우기</ToolbarButton>
      </div>

      <div className="admin-html-editor__tool-group admin-html-editor__tool-group--end">
        <ToolbarButton title="실행 취소" disabled={visualEditorDisabled || !editor?.can().undo()} onClick={() => editor?.chain().focus().undo().run()}>↶</ToolbarButton>
        <ToolbarButton title="다시 실행" disabled={visualEditorDisabled || !editor?.can().redo()} onClick={() => editor?.chain().focus().redo().run()}>↷</ToolbarButton>
        {recoveredHtml ? <ToolbarButton title="텍스트로 저장된 HTML 태그 복구" onClick={recoverSource}>HTML 복구</ToolbarButton> : null}
        <ToolbarButton title={rawHtml ? '원본 HTML과 렌더링 미리보기 전환' : 'HTML 소스 직접 편집'} active={sourceMode} onClick={toggleSource}>{sourceMode ? (rawHtml ? '미리보기' : '편집 화면') : 'HTML'}</ToolbarButton>
      </div>
    </div>

    {sourceMode
      ? <textarea
          className="admin-html-editor__source"
          aria-label={`${ariaLabel} HTML 소스`}
          spellCheck="false"
          value={value || ''}
          onChange={(event) => onChangeRef.current?.(event.target.value)}
          style={{ minHeight }}
        />
      : rawHtml
        ? <div className="admin-html-editor__canvas" style={{ minHeight }}>
            <iframe
              title={`${ariaLabel} 렌더링 미리보기`}
              sandbox=""
              referrerPolicy="no-referrer"
              srcDoc={rawPreviewDocument(value)}
              style={{ display: 'block', width: '100%', minHeight, border: 0, background: '#fff' }}
            />
          </div>
        : <div className="admin-html-editor__canvas" style={{ minHeight }}>
            <EditorContent editor={editor} style={{ minHeight }} />
            {!editor && <div className="admin-html-editor__loading">에디터를 준비하는 중입니다.</div>}
          </div>}

    <div className="admin-html-editor__footer">
      <span>{footerMessage}</span>
      <span className={required && empty ? 'is-warning' : ''}>{required && empty ? '본문 입력 필요' : `${String(value || '').length.toLocaleString('ko-KR')}자`}</span>
    </div>
  </div>
}
